import { useState } from 'react';
import { useTimeout } from '@mentimeter/react-hooks';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { Image } from '@mentimeter/ragnar-ui/image';
import { useTranslations } from 'next-intl';
import { FadeIn, ScaleIn } from '../Transitions';
import { SecondsCountdown, toMMSS } from './SecondsCountdown';
import { CountdownClock } from './CountdownClock';

const RespondentsBubble = ({
  respondents,
  size = 96,
}: {
  respondents: number;
  size?: number;
}) => {
  return (
    <Box
      width={`${size}px`}
      height={`${size}px`}
      bg="primary"
      borderRadius="full"
      alignItems="center"
      justifyContent="center"
    >
      <Text color="onPrimary" fontSize="250" lineHeight="tight">
        {respondents}
      </Text>
    </Box>
  );
};

const QuestionImage = ({
  imageUrl,
  fill,
}: {
  imageUrl: string;
  fill?: boolean;
}) => {
  const props = fill
    ? { width: '100%', height: '100%', flex: '1 1 auto' }
    : {
        maxWidth: 600,
        maxHeight: 350,
      };
  return (
    <Box alignItems="center" justifyContent="center" {...props}>
      <Image
        src={imageUrl}
        extend={({ theme }) => ({
          overflow: 'hidden',
          borderRadius: `${theme.radii[2]}px`,
        })}
      />
    </Box>
  );
};

export const Countdown = ({
  type,
  imageUrl,
  shouldShowLegacyImage,
  everyoneHasVoted,
  respondents,
  timeOffset,
  startAt,
  endAt,
  children,
}: React.PropsWithChildren<{
  type: 'open' | 'choices';
  imageUrl?: string | undefined;
  shouldShowLegacyImage: boolean;
  everyoneHasVoted: boolean;
  respondents: number;
  timeOffset: number;
  startAt: number;
  endAt: number;
}>) => {
  const translate = useTranslations('presenting.legacy');

  const [state, setState] = useState<'countdown' | 'start' | 'end'>(
    'countdown',
  );

  const hasImageAndShouldShow = imageUrl && shouldShowLegacyImage;

  const diffFromNow = Date.now() + timeOffset;
  const timeRemainingUntilQuizStarts = startAt - diffFromNow;
  const timeRemainingUntilQuizEnds = endAt - diffFromNow;

  useTimeout(() => setState('start'), timeRemainingUntilQuizStarts);
  useTimeout(() => setState('end'), timeRemainingUntilQuizEnds);

  return (
    <Box width="100%" height="100%">
      <Box
        width="100%"
        height="100%"
        flex="1 1 auto"
        alignItems="center"
        justifyContent="center"
      >
        {state === 'countdown' ? (
          <FadeIn key="countdown-fade-in">
            <Box alignItems="center">
              {hasImageAndShouldShow ? (
                <>
                  <FadeIn key="countdown-image-fade-in">
                    <QuestionImage imageUrl={imageUrl} />
                  </FadeIn>
                  <SecondsCountdown
                    timeRemaining={timeRemainingUntilQuizStarts}
                  >
                    {(seconds) => (
                      <Text
                        color="text"
                        fontSize="400"
                        fontWeight="semiBold"
                        mt={3}
                      >
                        {seconds.toString()}
                      </Text>
                    )}
                  </SecondsCountdown>
                </>
              ) : (
                <Box
                  my={3}
                  width="400px"
                  height="400px"
                  alignItems="center"
                  justifyContent="center"
                >
                  <CountdownClock
                    id="quiz-countdown"
                    duration={timeRemainingUntilQuizStarts}
                  />
                </Box>
              )}
              <Text
                color="text"
                fontSize="175"
                fontWeight="semiBold"
                textAlign="center"
              >
                {translate('countdown.look_at_phone')}
              </Text>
            </Box>
          </FadeIn>
        ) : (
          <>
            {/* Offset vertically to prevent image to overlap with bar chart */}
            <Box
              zIndex={2}
              width={hasImageAndShouldShow ? '100%' : 'auto'}
              height={hasImageAndShouldShow ? '100%' : 'auto'}
              position="absolute"
              alignItems="center"
            >
              {everyoneHasVoted ? (
                <ScaleIn key="everyone-has-voted-scale-in">
                  <Text color="text" fontSize="250" fontWeight="semiBold">
                    {translate('countdown.voting_complete')}
                  </Text>
                </ScaleIn>
              ) : hasImageAndShouldShow ? (
                <>
                  <Box mb={2} zIndex={2}>
                    <FadeIn>
                      <RespondentsBubble size={70} respondents={respondents} />
                    </FadeIn>
                  </Box>
                  <QuestionImage imageUrl={imageUrl} fill={type === 'open'} />
                </>
              ) : (
                <Box gap="space4" alignItems="center">
                  <FadeIn>
                    <SecondsCountdown
                      timeRemaining={timeRemainingUntilQuizEnds}
                    >
                      {(seconds) => (
                        <Text color="text" fontSize="400" fontWeight="semiBold">
                          {toMMSS(seconds)}
                        </Text>
                      )}
                    </SecondsCountdown>
                  </FadeIn>
                  <ScaleIn>
                    <RespondentsBubble respondents={respondents} />
                  </ScaleIn>
                </Box>
              )}
            </Box>
            {children}
            {hasImageAndShouldShow && (
              <Box
                position="absolute"
                bottom={0}
                style={{ transform: 'translateY(100%)' }}
              >
                <SecondsCountdown timeRemaining={timeRemainingUntilQuizEnds}>
                  {(seconds) => (
                    <Text color="text" fontSize="400" fontWeight="semiBold">
                      {toMMSS(seconds)}
                    </Text>
                  )}
                </SecondsCountdown>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
